import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {MPProperties} from 'common/constants';
import {emitTrackEvent} from 'common/util/dom';
import {FormattedProperty} from 'common/util/format-property';
import {TrackedEvents, TrackedProperties} from 'common/report/util/profile/tracking';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {UserSummaryPropertyVariables} from './types';
import {template} from './template';
import './index.styl';

interface ProfileUserSummaryState {
  showConfirmDeleteProfile: boolean;
}

export interface ProfileUserSummaryJsxProps {
  attrs: ProfileUserSummaryAttrs;
}

export interface ProfileUserSummaryAttrs {
  aliases: string[];
  'aliases-loading': boolean;
  'data-group-id'?: string | number;
  'distinct-id': string;
  'aliases-error': boolean;
  'has-delete-permissions': boolean;
  'hide-delete'?: boolean;
  'profile-exists': boolean;
  'profile-summary': UserSummaryPropertyVariables;
  'project-id': number;
  'sidebar-mode'?: boolean;
  'user-properties': {[name: string]: FormattedProperty};
  'user-properties-loading': boolean;
  'workspace-id': number;
}

export class ProfileUserSummary extends Component<ProfileUserSummaryState, ProfileUserSummaryAttrs> {
  static get attrsSchema(): AttrsSchema<ProfileUserSummaryAttrs> {
    return {
      ...super.attrsSchema,
      aliases: `json`,
      'aliases-loading': `boolean`,
      'data-group-id': `string`,
      'distinct-id': `string`,
      'aliases-error': `boolean`,
      'has-delete-permissions': `boolean`,
      'hide-delete': `boolean`,
      'profile-exists': `boolean`,
      'profile-summary': `json`,
      'project-id': `number`,
      'sidebar-mode': `boolean`,
      'user-properties': `json`,
      'user-properties-loading': `boolean`,
      'workspace-id': `number`,
    };
  }

  get config(): ConfigOptions<ProfileUserSummaryState> {
    return {
      template,
      defaultState: {
        showConfirmDeleteProfile: false,
      },
      helpers: {
        hasUnsubscribed: (): boolean => Boolean(this.attr(`user-properties`)[MPProperties.Unsubscribed]),
        getDeleteProfileButtonAttrs: (): {buttonAttrs: any; tooltip: string} => {
          const {'profile-exists': profileExists, 'has-delete-permissions': hasDeletePermissions} = this.attrs();

          let tooltip = `Delete Profile`;

          if (!profileExists) {
            tooltip = `Cannot delete because profile properties are not set on the profile.`;
          } else if (!hasDeletePermissions) {
            tooltip = `Only an Admin or Owner can delete a profile.`;
          }

          return {
            buttonAttrs: {
              theme: ButtonTheme.Tertiary,
              type: ButtonType.Destructive,
              disabled: !profileExists || !hasDeletePermissions,
            },
            tooltip,
          };
        },
        openDeleteProfileConfirmation: (): void => {
          this.update({
            showConfirmDeleteProfile: true,
          });
        },
        handleModalButton: (ev): void => {
          const {
            detail: {action},
          } = ev;
          if (action === `confirm`) {
            this.trackEvent(TrackedEvents.DeleteProfile);
            this.dispatchEvent(new CustomEvent(`deleteProfile`));
          }

          this.update({
            showConfirmDeleteProfile: false,
          });
        },
      },
    };
  }

  trackEvent(eventName: TrackedEvents, props: TrackedProperties = {}): void {
    emitTrackEvent(this, {eventName, props});
  }
}

export const ProfileUserSummaryEl = defineAndMakeJsxFactory<ProfileUserSummaryJsxProps>(
  `profile-user-summary`,
  ProfileUserSummary,
);
export const ProfileUserSummaryReact = panel2react<ProfileUserSummaryJsxProps>(
  `profile-user-summary`,
  ProfileUserSummary,
);
